/**
 * AlternatesSection — editable alternate answers for 4 column answers + 1 final solution.
 *
 * Input fields show text in uppercase, support spaces and commas freely.
 * Comma-separated entries are parsed on blur (not on every keystroke).
 * Tooltip shows the full uppercase text including spaces.
 */

import React, { useState, useCallback, useEffect } from 'react';
import type { PuzzleJSON } from '../../types';
import type { EditorActions } from './useEditorState';
import styles from './AlternatesSection.module.css';

interface AlternatesSectionProps {
  puzzle: PuzzleJSON;
  actions: EditorActions;
}

/** Split on commas, preserving spaces within each entry */
function parseAlternates(csv: string): string[] {
  return csv.split(',').map(s => s.replace(/^\s+|\s+$/g, '')).filter(s => s.length > 0);
}

function joinAlternates(alts: string[]): string {
  return alts.join(', ');
}

/** A single alternates input that uses local state while editing */
const AlternatesInput: React.FC<{
  value: string[];
  onChange: (alts: string[]) => void;
}> = ({ value, onChange }) => {
  const canonical = joinAlternates(value);
  const [localText, setLocalText] = useState(canonical);
  const [focused, setFocused] = useState(false);

  // Sync from external changes (e.g., load puzzle) when not focused
  useEffect(() => {
    if (!focused) {
      setLocalText(canonical);
    }
  }, [canonical, focused]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    const parsed = parseAlternates(localText);
    onChange(parsed);
    // Normalize the display text
    setLocalText(joinAlternates(parsed));
  }, [localText, onChange]);

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  return (
    <input
      className={styles.input}
      value={localText}
      onChange={e => setLocalText(e.target.value)}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder="alt1, alt2, ..."
      title={localText.toUpperCase() || 'No alternates'}
    />
  );
};

const AlternatesSection: React.FC<AlternatesSectionProps> = ({ puzzle, actions }) => {
  return (
    <div className={styles.section}>
      {/* Row 1: Column answers */}
      <div className={styles.row}>
        {puzzle.columns.map((col, colIdx) => {
          const pair2 = col.pairs[2];
          const answerText = pair2 ? pair2.clues[pair2.hiddenIndex].trim() : '';
          const hasAnswer = answerText.length > 0;
          return (
            <div key={colIdx} className={styles.field}>
              <span className={hasAnswer ? styles.fieldLabelSet : styles.fieldLabel}>
                {hasAnswer ? answerText.toUpperCase() : `Column ${colIdx + 1}`}
              </span>
              <AlternatesInput
                value={col.answerAlternates}
                onChange={alts => actions.setAlternates(colIdx, alts)}
              />
            </div>
          );
        })}
      </div>
      {/* Row 2: Final solution — spans full width aligned with columns */}
      <div className={styles.row}>
        <div className={styles.solutionField}>
          <span className={puzzle.solution.primary.trim() ? styles.fieldLabelSet : styles.fieldLabel}>
            {puzzle.solution.primary.trim()
              ? puzzle.solution.primary.toUpperCase()
              : 'Final Solution'}
          </span>
          <AlternatesInput
            value={puzzle.solution.alternates}
            onChange={alts => actions.setSolutionAlternates(alts)}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(AlternatesSection);
